import classNames from 'classnames';
import { Link as GatsbyLink } from 'gatsby';
import React, { CSSProperties, ReactNode } from 'react';

import { ReactComponent as RightArrow } from '../../assets/right-arrow.svg';
import {
	badge as badgeStyle,
	cardContainer as cardContainerStyle,
	figure as figureStyle,
	hover as hoverStyle,
	imageWrapper as imageWrapperStyle,
	innerContainer as innerContainerStyle,
	innerGrid as gridStyle,
	link as linkStyle,
	smallTitle as smallTitleStyle,
	summaryContainer as summaryContainerStyle,
} from './style.module.css';

export interface RelatedContentCardProps {
	title?: string;
	subtitle?: string;
	smallTitle?: boolean;
	summary?: string;
	renderImage?(): ReactNode;
	imageTitle?: string;
	badge?: string;
	linkLabel?: string;
	href?: string;
	openInNewTab?: boolean;
}

interface LinkWrapperProps {
	className: string;
	to?: string;
	title?: string;
	children: ReactNode;
	target?: string;
}

// @todo need a better technique to allow non-gatsby links here, render prop or smarter link component
const LinkWrapper = ({ children, className, to, target }: LinkWrapperProps) => {
	if (!to) return <div className={className}>{children}</div>;
	if (target)
		return (
			<a href={to} className={className} target={target}>
				{children}
			</a>
		);
	return (
		<GatsbyLink to={to} className={className}>
			{children}
		</GatsbyLink>
	);
};

const GridLinkWrapper = React.forwardRef<
	HTMLDivElement,
	Partial<LinkWrapperProps> & { style: CSSProperties }
>(
	(
		{ children, className, to, title, style }: Partial<LinkWrapperProps> & { style: CSSProperties },
		ref
	) => {
		if (!to) return <div className={className}>{children}</div>;
		return (
			<div ref={ref} style={style}>
				<GatsbyLink to={to} className={className} aria-label={title}>
					{children}
				</GatsbyLink>
			</div>
		);
	}
);

/** A vertically stacked card containing one or more of: image, title, summary, paragraph text, link with animated arrow */
export const RelatedContentCard = ({
	title,
	subtitle,
	smallTitle = false,
	summary,
	linkLabel,
	renderImage,
	imageTitle,
	badge,
	href,
	openInNewTab = false,
}: RelatedContentCardProps): JSX.Element => {
	return (
		<LinkWrapper
			to={href}
			className={classNames(cardContainerStyle, !!href && hoverStyle)}
			target={openInNewTab ? '_blank' : undefined}
		>
			{/* Image */}
			{!!renderImage && (
				<div className={innerContainerStyle}>
					<figure className={figureStyle}>
						<div className={imageWrapperStyle}>{renderImage()}</div>
						{!!badge && <span className={badgeStyle}>{badge}</span>}
					</figure>
				</div>
			)}
			<div className={innerContainerStyle}>
				{/* Title */}
				{!!title && <h1 className={smallTitle ? smallTitleStyle : undefined}>{title}</h1>}

				{/* Subtitle */}
				{!!subtitle && <h2>{subtitle}</h2>}

				<div className={summaryContainerStyle}>
					{/* Summary Paragraph */}
					{!!summary && <p>{summary}</p>}
				</div>

				{/* Link with arrow */}
				{!!href && (
					<div className={linkStyle}>
						{!!linkLabel && <span>{linkLabel}</span>}
						<RightArrow />
					</div>
				)}
			</div>
		</LinkWrapper>
	);
};

type HighlightTitleProps = Pick<RelatedContentCardProps, 'title' | 'smallTitle' | 'href'> & {
	hovering?: boolean;
	style: CSSProperties;
};

export const HighlightTitle = React.forwardRef<HTMLHeadingElement, HighlightTitleProps>(
	({ title, smallTitle = false, href, hovering, style }: HighlightTitleProps, ref): JSX.Element => {
		return (
			<GridLinkWrapper
				ref={ref}
				className={classNames(cardContainerStyle, !!href && hovering && hoverStyle)}
				to={href}
				style={style}
			>
				<div className={classNames(innerContainerStyle, gridStyle)}>
					{!!title && <h1 className={smallTitle ? smallTitleStyle : undefined}>{title}</h1>}
				</div>
			</GridLinkWrapper>
		);
	}
);

type HighlightSummaryProps = Pick<RelatedContentCardProps, 'href' | 'summary'> & {
	hovering?: boolean;
	style: CSSProperties;
};

export const HighlightSummary = React.forwardRef<HTMLParagraphElement, HighlightSummaryProps>(
	({ href, summary, hovering, style }: HighlightSummaryProps, ref): JSX.Element => {
		return (
			<GridLinkWrapper
				ref={ref}
				className={classNames(cardContainerStyle, !!href && hovering && hoverStyle)}
				to={href}
				style={style}
			>
				<div className={classNames(innerContainerStyle, gridStyle)}>
					<div className={summaryContainerStyle}>
						{/* Summary Paragraph */}
						{!!summary && <p>{summary}</p>}
					</div>
				</div>
			</GridLinkWrapper>
		);
	}
);

type HighlightLinkProps = Pick<RelatedContentCardProps, 'href' | 'linkLabel' | 'title'> & {
	hovering?: boolean;
	style: CSSProperties;
};

export const HighlightLink = React.forwardRef<HTMLDivElement, HighlightLinkProps>(
	({ href, linkLabel, hovering, style, title }: HighlightLinkProps, ref): JSX.Element => {
		return (
			<GridLinkWrapper
				ref={ref}
				className={classNames(cardContainerStyle, !!href && hovering && hoverStyle)}
				to={href}
				style={style}
				title={title}
			>
				<div className={classNames(innerContainerStyle, gridStyle)}>
					{!!href && (
						<div className={linkStyle}>
							{!!linkLabel && <span>{linkLabel}</span>}
							<RightArrow />
						</div>
					)}
				</div>
			</GridLinkWrapper>
		);
	}
);
